// src/context/site/KbJobBanner.jsx
//
// The "something is happening" surface while a KB job runs. The chip alone is
// an 11px pill and was missed by the first person who tested the page, so the
// two busy states also get a full-width banner: a two-step indicator
// (scansione → indicizzazione), an INDETERMINATE progress bar and an elapsed
// counter. ⛔ The bar is indeterminate on purpose — GET /kb-status publishes no
// numbers (no page count, no percentage), and inventing one would lie.
//
// Steps: a `reload` runs discovery THEN indexing (contract: "rescans the site
// for new pages first, promotes the refreshed list, and then re-indexes"); a
// `reindex` only indexes. `Discovering` always implies the two-step shape.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Check } from 'lucide-react';
import { COLORS, FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { isKbBusy } from './useKbStatus.js';

const TICK_MS = 1000;

/** "45 s" / "3 min" / "1 h 12 min" — coarse on purpose, it is a reassurance, not a stopwatch. */
export function formatElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h} h ${m % 60} min`;
}

function useElapsed(startedAt, running) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [running, startedAt]);
  if (!running || !startedAt) return null;
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return null;
  return formatElapsed(now - start);
}

export default function KbJobBanner({ status, compact = false }) {
  const { t } = useTranslation('context');
  const state = status?.state;
  const running = isKbBusy(state);
  const elapsed = useElapsed(status?.started_at, running);
  if (!running) return null;

  const twoSteps = state === 'Discovering' || status?.kind === 'reload';
  const steps = twoSteps
    ? [
      { key: 'discover', label: t('site.job.stepDiscover'), done: state === 'Indexing', active: state === 'Discovering' },
      { key: 'index', label: t('site.job.stepIndex'), done: false, active: state === 'Indexing' },
    ]
    : [{ key: 'index', label: t('site.job.stepIndex'), done: false, active: true }];
  const title = state === 'Discovering' ? t('site.job.titleDiscovering') : t('site.job.titleIndexing');

  return (
    <div
      data-testid="kb-job-banner"
      data-state={state}
      style={{
        marginTop: compact ? '12px' : '14px',
        padding: compact ? '12px 14px' : '14px 16px',
        borderRadius: RADII.card,
        background: SEMANTIC.info.bg,
        color: COLORS.ink,
        fontFamily: FONT_SANS,
        fontSize: '12px',
        lineHeight: 1.5,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <Loader2 size={16} style={{ color: SEMANTIC.info.fg, animation: 'kb-spin 0.8s linear infinite', flex: 'none' }} aria-hidden="true" />
        <strong style={{ fontSize: '13px', color: SEMANTIC.info.fg }}>{title}</strong>
        {elapsed && (
          <span style={{ color: COLORS.textSecondary }} data-testid="kb-job-elapsed">
            · {t('site.job.elapsed', { elapsed })}
          </span>
        )}
      </div>

      <ol style={{ listStyle: 'none', margin: '10px 0 0', padding: 0, display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
        {steps.map((s, i) => (
          <li
            key={s.key}
            data-testid={`kb-job-step-${s.key}`}
            data-active={s.active ? 'true' : 'false'}
            data-done={s.done ? 'true' : 'false'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              fontWeight: s.active ? 700 : 500,
              color: s.active ? SEMANTIC.info.fg : (s.done ? SEMANTIC.verde.fg : COLORS.textSecondary),
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '18px', height: '18px', borderRadius: RADII.pill,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '10px', fontWeight: 700,
                background: s.done ? SEMANTIC.verde.bg : (s.active ? SEMANTIC.info.fg : COLORS.surface),
                color: s.done ? SEMANTIC.verde.fg : (s.active ? COLORS.surface : COLORS.textSecondary),
                border: s.active || s.done ? 'none' : `1px solid ${COLORS.borderStrong}`,
              }}
            >
              {s.done ? <Check size={11} /> : i + 1}
            </span>
            {s.label}
          </li>
        ))}
      </ol>

      {/* Indeterminate bar: no aria-valuenow on purpose (the API publishes no progress). */}
      <div
        role="progressbar"
        aria-label={title}
        aria-valuetext={elapsed ? t('site.job.elapsed', { elapsed }) : title}
        style={{
          position: 'relative', overflow: 'hidden', height: '6px', marginTop: '10px',
          borderRadius: RADII.pill, background: COLORS.surface,
        }}
      >
        <div
          style={{
            position: 'absolute', top: 0, bottom: 0, width: '35%', borderRadius: RADII.pill,
            background: SEMANTIC.info.fg, animation: 'kb-slide 1.6s ease-in-out infinite',
          }}
        />
      </div>

      {!compact && <p style={{ margin: '10px 0 0', color: COLORS.textSecondary }}>{t('site.busyHint')}</p>}

      <style>{`
        @keyframes kb-spin { to { transform: rotate(360deg); } }
        @keyframes kb-slide { 0% { left: -35%; } 100% { left: 100%; } }
      `}</style>
    </div>
  );
}
